import React from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  GraduationCap,
  Building2,
  Users,
  CreditCard,
} from 'lucide-react';
import type { StudentUser } from '@/services/superAdminUserService';

interface ViewStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: StudentUser;
}

const GENDER_LABELS: Record<string, string> = {
  MASCULIN: 'Masculin',
  FEMININ: 'Feminin',
  M: 'Masculin',
  F: 'Feminin',
};

const STATUS_LABELS: Record<string, string> = {
  ENROLLED: 'Inscrit',
  GRADUATED: 'Diplome',
  WITHDRAWN: 'Retire',
  SUSPENDED: 'Suspendu',
  ON_LEAVE: 'En conge',
  PENDING: 'En attente',
  ACTIVE: 'Actif',
};

const STATUS_COLORS: Record<string, string> = {
  ENROLLED: 'bg-green-100 text-green-700',
  GRADUATED: 'bg-blue-100 text-blue-700',
  WITHDRAWN: 'bg-gray-100 text-gray-700',
  SUSPENDED: 'bg-red-100 text-red-700',
  ON_LEAVE: 'bg-yellow-100 text-yellow-700',
  PENDING: 'bg-orange-100 text-orange-700',
  ACTIVE: 'bg-green-100 text-green-700',
};

export const ViewStudentModal: React.FC<ViewStudentModalProps> = ({
  isOpen,
  onClose,
  student,
}) => {
  if (!isOpen) return null;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-5 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <User className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  {student.firstName} {student.lastName}
                </h2>
                <p className="text-green-100 font-mono">{student.studentCode}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Status Badge */}
          <div className="mt-4 flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[student.status] || 'bg-gray-100 text-gray-700'}`}>
              {STATUS_LABELS[student.status] || student.status}
            </span>
            {student.academicYear && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
                {student.academicYear.name}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Contact Information */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Contact
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">
                    {student.email || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Phone className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Telephone</p>
                  <p className="text-sm font-medium text-gray-900">
                    {student.phone || '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Informations personnelles
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Date de naissance</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(student.dateOfBirth)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <MapPin className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Lieu de naissance</p>
                  <p className="text-sm font-medium text-gray-900">
                    {student.placeOfBirth || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <User className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Genre</p>
                  <p className="text-sm font-medium text-gray-900">
                    {GENDER_LABELS[student.gender || ''] || student.gender || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <CreditCard className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">CNIE</p>
                  <p className="text-sm font-medium text-gray-900">
                    {student.cnie || '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Academic Information */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Informations academiques
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <GraduationCap className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Programme</p>
                  <p className="text-sm font-medium text-gray-900">
                    {student.program?.name || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <GraduationCap className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Diplome</p>
                  <p className="text-sm font-medium text-gray-900">
                    {student.diplomeInscrit || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Building2 className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Campus</p>
                  <p className="text-sm font-medium text-gray-900">
                    {student.campus?.name || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Niveau</p>
                  <p className="text-sm font-medium text-gray-900">
                    {student.niveauFormation ? `Niveau ${student.niveauFormation}` : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Address */}
          {(student.address || student.city) && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Adresse
              </h3>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    {student.address && (
                      <p className="text-sm text-gray-900">{student.address}</p>
                    )}
                    <p className="text-sm text-gray-600">
                      {[student.city, student.postalCode].filter(Boolean).join(', ') || '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Parents */}
          {student.parents && student.parents.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Parents / Tuteurs
              </h3>
              <div className="space-y-3">
                {student.parents.map((parent) => (
                  <div
                    key={parent.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {parent.firstName} {parent.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {parent.relationship} {parent.isPrimary && '(Principal)'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {parent.phone && (
                        <p className="text-sm text-gray-600">{parent.phone}</p>
                      )}
                      {parent.email && (
                        <p className="text-xs text-gray-500">{parent.email}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ViewStudentModal;
